import { internalFetch } from '../utils/fetchHelper';

export type CardSlotDisplay = 'block' | 'inline';
export type CardSlotAlign = 'left' | 'center' | 'right';
export type CardSlotFontSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ICardSlotStyle {
  display?: CardSlotDisplay;
  align?: CardSlotAlign;
  fontSize?: CardSlotFontSize;
  color?: string;
}

export interface ICardSlot {
  id: string;
  label: string;
  order: number;
  style?: ICardSlotStyle;
}

export type CardStaticFieldKey =
  | 'id'
  | 'code'
  | 'name'
  | 'description'
  | 'status'
  | 'entityTypeName';
export type CardStaticFieldPosition = 'start' | 'end';

export interface ICardStaticFieldBinding {
  key: CardStaticFieldKey;
  slotId: string;
  position: CardStaticFieldPosition;
}

export interface ICardLayoutDefinition {
  slots: ICardSlot[];
  staticFields?: ICardStaticFieldBinding[];
}

export interface IField {
  repeatingMode?: string;
  name: string;
  fieldType: string;
  options?: any;
  label: string;
  hint?: string;
  readonly?: boolean;
  readonlyForContractors?: boolean;
  tabName?: string;
  lookupList?: string;
  multiSelect?: boolean;
  hiden?: boolean;
  allowAppend?: boolean;
  reference?: string;
  dependees?: string;
  mandatory?: boolean;
  showInGrid?: boolean;
  filterable?: boolean;
  showInDefaultFilters?: boolean;
  showInClient?: boolean;
  showInReport?: boolean;
  cardSlotId?: string;
  cardSlotOrder?: number;
  optionsLayout?: string;
  labelAbove?: boolean;
  fullWidth?: boolean;
  copyToOrderedEntity?: boolean;
  permissions?: string[];
  rows?: number;
  effect?: string; // "Show" | "Hide"
  parentName?: string;
  isCollapsed?: boolean;
}

export interface ILookupValue {
  key: string;
  value: string;
  header?: boolean;
}

export interface ILookup {
  name: string;
  values: ILookupValue[];
}

export interface IValidationRule {
  rule: string;
  errorMessage: string;
}

export interface IValidation {
  field: string;
  rules: IValidationRule[];
}

export interface IFieldsTab {
  order: number;
  tabName: string;
  caption: string;
  hidden?: boolean;
}

export interface IEffect {
  fields: string[];
  effectType: string; // "Show" | "Hide"
}

export interface IRule {
  field: string;
  operator: string;
  value: string;
}

export interface ICondition {
  combinator: string; // "And" | "Or"
  not: boolean;
  rules: IRule[];
}

export interface IInteraction {
  name: string;
  description: string;
  condition: ICondition;
  effects: IEffect[];
}

export interface IMapOptions {
  coordinateField?: string;
  markerField?: string;
}

export interface IMetadata {
  id?: number;
  status?: string;
  statusChangedAt?: string;
  statusChangedBy?: number;
  lookups: ILookup[];
  fields: IField[];
  validations: IValidation[];
  tabs?: IFieldsTab[];
  interactions: IInteraction[];
  cardLayout?: ICardLayoutDefinition;
  mapOptions?: IMapOptions;
}

export interface IEntityMetadata {
  metadataId?: number;
  status?: string;
  statusChangedAt?: string;
  statusChangedBy?: number;
  metadata?: IMetadata;
  lookups?: ILookup[];
  fields?: IField[];
  validations?: IValidation[];
  tabs?: IFieldsTab[];
  interactions?: IInteraction[];
}

export interface IUpdateMetadataResponse {
  version: number;
}

export interface IEntitiesMetadatasResponse {
  metadatas: Record<string, IEntityMetadata>;
}

export interface IArchivedMetadatasResponse {
  metadatas: Record<string, string>;
}

export interface IUpdateMetadataStatusRequest {
  status: string;
  action?: string;
  comment?: string;
}

export interface IUpdateMetadataStatusResponse {
  metadataId: number;
  status: string;
}

export const getMetadata = async (
  abortController: AbortController,
  entityType?: string,
  allFields?: boolean,
  name?: string
): Promise<IMetadata> => {
  const metadata = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/metadata?entity=${entityType}${
      allFields ? `&allFields=${allFields}` : ''
    }${name ? `&name=${name}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );

  return normalizeMetadataForApi(
    metadata as IMetadata | IEntityMetadata | null | undefined
  );
};

export const getSpecificMetadata = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  version: number,
  allFields = true
): Promise<IMetadata> => {
  const qs = new URLSearchParams({
    entityType,
    name,
    version: String(version),
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  if (allFields) qs.set('allFields', 'true');
  const metadata = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/specificMetadata?${qs}`,
    'GET',
    abortController
  );
  return normalizeMetadataForApi(
    metadata as IMetadata | IEntityMetadata | null | undefined
  );
};

export const revertMetadata = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  version: number,
  metadata: IMetadata
): Promise<IUpdateMetadataResponse> => {
  const normalized = normalizeMetadataForApi(metadata);
  const qs = new URLSearchParams({
    entityType,
    name,
    version: String(version),
    code: import.meta.env.VITE_BACKEND_API_KEY,
  });
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/revertMetadata?${qs}`,
    'POST',
    abortController,
    JSON.stringify({ metadata: normalized })
  );
};

export const getEntitiesMetadatas = async (
  abortController: AbortController,
  entityTypes?: string[]
): Promise<IEntitiesMetadatasResponse> => {
  const qs = new URLSearchParams();
  if (entityTypes && entityTypes.length > 0) {
    qs.set('entityTypes', entityTypes.join(','));
  }
  qs.set('code', import.meta.env.VITE_BACKEND_API_KEY);
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/latestMetadatas?${qs.toString()}`,
    'GET',
    abortController
  );
};

export interface IMetadataListItem {
  id: number;
  name: string;
  entity: string;
  version: number;
  createdAt: string;
  status: string;
  statusChangedBy: number | null;
  statusDate: string;
  comment: string | null;
}

export interface IMetadatasPagedResponse {
  isSuccess: boolean;
  message: string | null;
  correlationId: string;
  items: IMetadataListItem[];
  // Optional — BE may add later. Consumers should fall back gracefully.
  pageCount?: number;
  isLastPage?: boolean;
  rowsCount?: number;
}

export const getMetadatasPaged = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string,
  filters?: string
): Promise<IMetadatasPagedResponse> => {
  const qs = new URLSearchParams();
  qs.set('pageSize', String(pageSize));
  qs.set('pageNo', String(pageNo));
  if (orderBy) qs.set('orderBy', orderBy);
  if (search) qs.set('search', search);
  if (filters) qs.set('filters', filters);
  qs.set('code', import.meta.env.VITE_BACKEND_API_KEY);
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/metadatas?${qs.toString()}`,
    'GET',
    abortController
  );
};
export const getLatestMetadatasNames = async (
  abortController: AbortController,
  entityTypes?: string[]
): Promise<string[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/latestMetadatasNames?${
      entityTypes ? `entityTypes=${entityTypes.join(',')}` : ''
    }&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};
export const getArchivedMetadatas = async (
  abortController: AbortController
): Promise<IArchivedMetadatasResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/archivedMetadatas?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

function createEmptyMetadata(): IMetadata {
  return {
    lookups: [],
    fields: [],
    validations: [],
    interactions: [],
  };
}

function normalizeValidationRules(rules: unknown): IValidationRule[] {
  if (!Array.isArray(rules)) {
    return [];
  }

  return rules
    .map((rule): IValidationRule | null => {
      if (typeof rule === 'string') {
        return { rule, errorMessage: '' };
      }

      if (rule && typeof rule === 'object') {
        const parsedRule =
          typeof (rule as { rule?: unknown }).rule === 'string'
            ? (rule as { rule: string }).rule
            : '';
        const parsedErrorMessage =
          typeof (rule as { errorMessage?: unknown }).errorMessage === 'string'
            ? (rule as { errorMessage: string }).errorMessage
            : '';

        if (!parsedRule) {
          return null;
        }

        return {
          rule: parsedRule,
          errorMessage: parsedErrorMessage,
        };
      }

      return null;
    })
    .filter((rule): rule is IValidationRule => rule !== null);
}

function normalizeValidations(validations: unknown): IValidation[] {
  if (!Array.isArray(validations)) {
    return [];
  }

  return validations
    .map((validation): IValidation | null => {
      if (!validation || typeof validation !== 'object') {
        return null;
      }

      const field =
        typeof (validation as { field?: unknown }).field === 'string'
          ? (validation as { field: string }).field
          : '';

      if (!field) {
        return null;
      }

      return {
        field,
        rules: normalizeValidationRules(
          (validation as { rules?: unknown }).rules ?? []
        ),
      };
    })
    .filter((validation): validation is IValidation => validation !== null);
}

function normalizeMetadataForApi(
  metadata?: IMetadata | IEntityMetadata | null
): IMetadata {
  if (!metadata || typeof metadata !== 'object') {
    return createEmptyMetadata();
  }

  const entityMetadata = metadata as IEntityMetadata;
  const normalizedInput =
    entityMetadata.metadata && typeof entityMetadata.metadata === 'object'
      ? entityMetadata.metadata
      : (metadata as IMetadata);

  const legacyMetadata = normalizedInput as IMetadata & { slots?: ICardSlot[] };
  const { slots, ...rest } = legacyMetadata;

  const normalizedRest: IMetadata = {
    ...rest,
    lookups: rest.lookups ?? [],
    fields: rest.fields ?? [],
    validations: normalizeValidations(rest.validations),
    interactions: rest.interactions ?? [],
  };

  if (normalizedRest.cardLayout?.slots?.length) {
    return normalizedRest;
  }

  if (Array.isArray(slots) && slots.length > 0) {
    return {
      ...normalizedRest,
      cardLayout: {
        slots,
      },
    };
  }

  return normalizedRest;
}

export const updateMetadata = async (
  abortController: AbortController,
  entityType: string,
  metadata: IMetadata,
  name?: string
): Promise<IUpdateMetadataResponse> => {
  const normalizedMetadata = normalizeMetadataForApi(metadata);

  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadata?entityType=${entityType}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify({
      name: name,
      metadata: normalizedMetadata,
    })
  );
};

export const updateMetadataStatus = async (
  abortController: AbortController,
  metadataId: number,
  request: IUpdateMetadataStatusRequest
): Promise<IUpdateMetadataStatusResponse> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadataStatus?metadataId=${metadataId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify(request)
  );
};

export interface IValidateMetadataResponse {
  errors: Record<string, string> | null;
  isSuccess: boolean;
  message: string | null;
  correlationId?: string;
}

export const validateMetadata = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  metadata: IMetadata
): Promise<IValidateMetadataResponse> => {
  const normalizedMetadata = normalizeMetadataForApi(metadata);

  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/validateMetadata?entityType=${encodeURIComponent(
      entityType
    )}&name=${encodeURIComponent(name)}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'POST',
    abortController,
    JSON.stringify({ metadata: normalizedMetadata }),
    undefined,
    undefined,
    [422]
  );
};

export interface IMetadataFieldEntities {
  field: string;
  entities: number[];
}

export interface IMetadataEntitiesResponse {
  fieldEntityIds: IMetadataFieldEntities[];
  isSuccess: boolean;
  message: string | null;
  correlationId?: string;
}

export const getMetadataEntities = async (
  abortController: AbortController,
  entityType: string,
  name: string,
  fields: string[]
): Promise<IMetadataEntitiesResponse> => {
  const fieldsParam = fields.join(',');
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/metadataEntities?entityType=${encodeURIComponent(
      entityType
    )}&name=${encodeURIComponent(name)}&fields=${encodeURIComponent(
      fieldsParam
    )}&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};
